// A browser-side event bus for the Debug Panel — a single, unified timeline of
// what the client is doing (transcript forwarded, flushes, signals applied,
// capture state) MERGED with the gateway's debug stream (reflex classify /
// verdict / delegate, drained over SSE by the panel and re-emitted here). A tiny
// dependency-free store in the spirit of sessionStore: subscribe + getSnapshot,
// a new array reference on each change so useSyncExternalStore re-renders.
//
// Observability only — nothing in the app depends on it. It is a module
// singleton so any layer (bridgeClient, the panel) can emit without plumbing.

export type PanelEventSource = 'browser' | 'gateway'

export interface PanelEvent {
  /** Local monotonic id — a stable React key and total order across sources. */
  id: number
  source: PanelEventSource
  t: number
  /** DebugEventKind on the gateway side; a free browser kind on the client side. */
  kind: string
  message: string
  data?: Record<string, unknown>
}

const MAX_EVENTS = 1000

let events: PanelEvent[] = []
let seq = 0
const listeners = new Set<() => void>()

function notify(): void {
  for (const l of listeners) l()
}

export function emitPanelEvent(
  source: PanelEventSource,
  kind: string,
  message: string,
  data?: Record<string, unknown>
): void {
  const event: PanelEvent = { id: seq++, source, t: Date.now(), kind, message }
  if (data) event.data = data
  // New array reference (getSnapshot identity contract); cap the ring.
  events = events.length >= MAX_EVENTS ? [...events.slice(1), event] : [...events, event]
  notify()
}

export function clearPanelEvents(): void {
  if (events.length === 0) return
  events = []
  notify()
}

export function subscribePanel(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getPanelEvents(): PanelEvent[] {
  return events
}
